import { motion } from 'framer-motion'
import { applicationSteps } from '../data/mockData'
import { Wrench, Home, Building } from 'lucide-react'

const Application = () => {
  return (
    <div className="min-h-screen pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
            <Wrench className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-gradient mb-6">Guide d'Application</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Application simple et efficace en deux phases. Aucun besoin d'eau pour le mélange.
          </p>
        </motion.div>

        {/* Section Applications multiples */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mb-16"
        >
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Applications Polyvalentes</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg border-l-4 border-blue-500">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Building className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Murs Extérieurs</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Isolation thermique optimale pour façades et murs extérieurs. 
                Appliquez directement sur la couche de dégrossissage au ciment.
              </p>
              <ul className="text-gray-600 space-y-2">
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  Façades et murs extérieurs
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  Isolation périphérique
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  Protection contre les ponts thermiques
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border-l-4 border-green-500">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <Home className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Murs Intérieurs</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Solution idéale pour l'isolation intérieure des murs, 
                permettant de réduire les déperditions thermiques.
              </p>
              <ul className="text-gray-600 space-y-2">
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  Murs intérieurs et cloisons
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  Isolation des pièces humides
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  Rénovation thermique
                </li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* Préparation du support */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="bg-yellow-50 rounded-2xl p-8 mb-12 border-l-4 border-yellow-500"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Préparation du Support</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Surface Requise</h3>
              <p className="text-gray-700 mb-4">
                Avant d'appliquer l'enduit isolant Ecotherm, assurez-vous qu'une couche de 
                <strong> dégrossissage au ciment</strong> a été réalisée sur la façade, 
                moellons/briques ou autre support.
              </p>
              <div className="space-y-2">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-yellow-600 text-sm font-bold">✓</span>
                  </div>
                  <p className="text-gray-700">Surface uniforme et aplanie</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-yellow-600 text-sm font-bold">✓</span>
                  </div>
                  <p className="text-gray-700">Évite la surconsommation de produit</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-yellow-600 text-sm font-bold">✓</span>
                  </div>
                  <p className="text-gray-700">Support propre et sec</p>
                </div>
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Supports Compatibles</h3>
              <div className="grid grid-cols-2 gap-3">
                {[
                  'Béton',
                  'Briques',
                  'Moellons',
                  'Parpaings',
                  'Ciment',
                  'Pierre naturelle'
                ].map((material, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                    <span className="text-gray-700 text-sm">{material}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Étapes d'application */}
        <div className="space-y-8 mb-16">
          {applicationSteps.map((step, index) => (
            <motion.div
              key={step.id}
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: index * 0.15 }}
              className="bg-white rounded-2xl shadow-lg overflow-hidden card-hover"
            >
              <div className="flex flex-col lg:flex-row">
                <div className="lg:w-1/3 bg-gradient-to-br from-green-600 to-green-700 p-8 flex items-center justify-center">
                  <div className="text-center text-white">
                    <div className="text-6xl font-bold mb-2">{step.step}</div>
                    <div className="text-xl font-semibold">{step.phase}</div>
                  </div>
                </div>
                <div className="lg:w-2/3 p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{step.title}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{step.description}</p>
                  <div className="space-y-3">
                    {step.details.map((detail, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-green-600 text-sm font-bold">{idx + 1}</span>
                        </div>
                        <p className="text-gray-700">{detail}</p>
                      </div>
                    ))}
                  </div>
                  {step.drying && (
                    <div className="mt-6 bg-green-50 rounded-lg p-4 border-l-4 border-green-600">
                      <p className="text-green-800 font-semibold">⏱️ Temps de séchage: {step.drying}</p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Mode d'emploi détaillé */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="bg-blue-50 rounded-2xl p-8 mb-12 border-l-4 border-blue-500"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Mode d'Emploi Détaillé</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Mélange et Application</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-blue-600 font-bold">1</span>
                  </div>
                  <div>
                    <p className="text-gray-700 font-medium">Mélange au malaxeur (700-900 tr/min) pendant 2 minutes</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-blue-600 font-bold">2</span>
                  </div>
                  <div>
                    <p className="text-gray-700 font-medium">Application à la lisseuse ou machine à projeter</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-blue-600 font-bold">3</span>
                  </div>
                  <div>
                    <p className="text-gray-700 font-medium">Première couche de 0,5 cm d'épaisseur</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Séchage et Finition</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-green-600 font-bold">4</span>
                  </div>
                  <div>
                    <p className="text-gray-700 font-medium">Séchage 24 heures (selon température/humidité)</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-green-600 font-bold">5</span>
                  </div>
                  <div>
                    <p className="text-gray-700 font-medium">Application de la deuxième couche</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-green-600 font-bold">6</span>
                  </div>
                  <div>
                    <p className="text-gray-700 font-medium">Épaisseur totale finale: 1 cm</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Outils nécessaires */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="bg-gradient-to-br from-green-50 to-green-100 rounded-3xl p-12 moroccan-pattern mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">Outils Nécessaires</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { 
                image: '/images/truelle-ergo-ronde-en-inox-magne.webp', 
                name: 'Truelle Inox',
                description: 'Truelle en acier inoxydable pour application'
              },
              { 
                image: '/images/media.jpg', 
                name: 'Malaxeur 700-900 RPM',
                description: 'Malaxeur électrique pour mélange homogène'
              },
              { 
                image: '/images/OIP (5).webp', 
                name: 'Machine à Projeter',
                description: 'Machine professionnelle pour projection'
              },
              { 
                image: '/images/OIP (6).webp', 
                name: 'Règle de Finition',
                description: 'Règle métallique pour finition parfaite'
              }
            ].map((tool, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: 0.7 + index * 0.1 }}
                className="bg-white rounded-xl p-6 text-center shadow-md hover:shadow-lg transition-shadow duration-300"
              >
                <div className="mb-4 flex justify-center">
                  <div className="w-20 h-20 rounded-full bg-gray-100 overflow-hidden flex items-center justify-center">
                    <img 
                      src={tool.image} 
                      alt={tool.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none'
                        e.target.nextSibling.style.display = 'flex'
                      }}
                    />
                    <div className="hidden w-full h-full items-center justify-center bg-green-100 text-green-600">
                      <Wrench className="w-8 h-8" />
                    </div>
                  </div>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{tool.name}</h3>
                <p className="text-sm text-gray-600">{tool.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Conseils importants */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.9 }}
          className="mt-16 bg-white rounded-2xl p-8 shadow-lg"
        >
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Conseils Importants</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-xl">⚠️</span>
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1">Surface Propre</h3>
                <p className="text-gray-600 text-sm">Assurez-vous que la surface est sèche et propre avant l'application</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-xl">💧</span>
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1">Pas d'Eau</h3>
                <p className="text-gray-600 text-sm">Ne jamais ajouter d'eau lors du mélange du produit</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-xl">🌡️</span>
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1">Conditions Climatiques</h3>
                <p className="text-gray-600 text-sm">Le temps de séchage varie selon la température ambiante</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-xl">📐</span>
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1">Épaisseur Uniforme</h3>
                <p className="text-gray-600 text-sm">Respectez l'épaisseur recommandée pour chaque couche</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default Application